/**
 * The embedded intake form ⇄ host page postMessage contract. The form posts
 * its content height (and that it was submitted); the script in the embed
 * snippet built here resizes the iframe to fit. Both halves live in this file
 * so the message shape cannot drift between them.
 */
export const FORM_EMBED_SOURCE = 'notifi-form';

export type FormEmbedMessage =
  | { source: typeof FORM_EMBED_SOURCE; type: 'resize'; height: number }
  | { source: typeof FORM_EMBED_SOURCE; type: 'submitted' };

/** Where the frame starts, and stays when the host strips the script. */
const INITIAL_HEIGHT = 720;

/** True when this page runs inside another page's iframe. */
export function isInFrame(): boolean {
  return typeof window !== 'undefined' && window.parent !== window;
}

/**
 * Post to the embedding host page. targetOrigin stays '*': the messages carry
 * only a height, and the host (any customer site) is unknowable here. The
 * snippet's script pins our origin and its own iframe.
 */
export function postFormToHost(message: FormEmbedMessage): void {
  if (!isInFrame()) return;
  try {
    window.parent.postMessage(message, '*');
  } catch {
    // A sandboxed or broken parent: the form still works at a fixed height.
  }
}

function escapeHtmlAttribute(value: string) {
  return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;');
}

/**
 * The HTML a tenant pastes into their website: the iframe, plus a small
 * script that sizes it to the form so there is no inner scrollbar. Without
 * the script (some site builders strip them) the iframe stays 720px tall and
 * the form scrolls inside it.
 */
export function formEmbedSnippet({
  origin,
  key,
  title,
}: {
  origin: string;
  key: string;
  title: string;
}): string {
  const encodedKey = encodeURIComponent(key);
  const iframe = `<iframe src="${origin}/form/${encodedKey}?embed=1" title="${escapeHtmlAttribute(title)}" style="width:100%;height:${INITIAL_HEIGHT}px;border:0;display:block" loading="lazy"></iframe>`;
  // ES5 only, no dependencies: it runs on whatever website the form is pasted
  // into. The iframe is found by the window that sent the message, not by an
  // id, so the same form pasted twice on one page sizes each copy.
  const script = [
    '(function(){',
    'window.addEventListener("message",function(e){',
    `if(e.origin!==${JSON.stringify(origin)}||!e.source)return;`,
    `var d=e.data;if(!d||d.source!==${JSON.stringify(FORM_EMBED_SOURCE)})return;`,
    'var fs=document.getElementsByTagName("iframe"),f=null;',
    'for(var i=0;i<fs.length;i++){if(fs[i].contentWindow===e.source){f=fs[i];break;}}',
    'if(!f)return;',
    'if(d.type==="resize"&&typeof d.height==="number"&&d.height>0){f.style.height=Math.ceil(d.height)+"px";}',
    'else if(d.type==="submitted"&&f.getBoundingClientRect().top<0){f.scrollIntoView({behavior:"smooth",block:"start"});}',
    '});',
    '})();',
  ].join('');
  return `${iframe}\n<script>${script}</script>`;
}
